import React, { useState, useEffect } from 'react';
import { Card } from 'antd';
import { formatPhoneNumber } from 'utils';
import styled from 'styled-components';
import { Loading } from '@app/react-common';
import axios, { AxiosRequestConfig } from 'axios';
import { IFrontEndInstructions } from '@bb/users';

import { Filelink } from 'filestack-js';
import { FILESTACK_API_KEY } from '@app/constants';

type ExternalAgents = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  orgName: string;
  orgPhone: string;
  frontEndInstructions: IFrontEndInstructions;
};

const MyAgent = ({ ExternalAuth, ErrorService }: any) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [agents, setAgents] = useState<ExternalAgents[] | null>(null);

  const { auth } = ExternalAuth;

  const getAvatar = (id: string) => {
    const src: any = new Filelink(id, FILESTACK_API_KEY);
    src.resize({ width: 300, height: 300 });
    return src.toString();
  };

  const DEFAULT_AVATAR =
    'https://cdn.filestackcontent.com/yWwQq12yR2a8pUdwujYB';

  const fetchAgents = async () => {
    const config: AxiosRequestConfig = {
      url: `/external/dash/${auth.dashboardId}/agents`,
      method: 'get',
    };
    try {
      const {
        data: { reps },
      } = await axios.request<{ reps: ExternalAgents[] }>(config);
      const temp = reps.map((rep: any) => {
        const avatar = rep.avatarFilestackId
          ? getAvatar(rep.avatarFilestackId)
          : DEFAULT_AVATAR;
        return { ...rep, avatar };
      });
      setAgents(temp);
    } catch (err) {
      ErrorService.toastyHTTPErr(err);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  const getHeader = () => `My Agent${agents?.length > 1 ? 's' : ''}`;
  return (
    <>
      <div className="normalheader transition animated fadeIn">
        <div className="hpanel">
          <div className="panel-body">
            <h2 className="font-light m-b-xs">
              <div className="row">
                <div className="col-md-12">{getHeader()}</div>
              </div>
            </h2>
          </div>
        </div>
      </div>
      <div className="content transition animated fadeIn">
        <div className="row">
          <div className="col-md-12">
            <div className="hpanel">
              {loading || agents === null ? (
                <Loading size="fa-4x" />
              ) : (
                <CardsContainer className="panel-body">
                  {agents.map((agent: any) => (
                    <StyledCard key={agent.email}>
                      <div className="row profile-edit-center">
                        {agent.avatar ? (
                          <div className="col-md-12">
                            <img
                              className="profile-edit-picture"
                              src={agent.avatar}
                              style={{ width: '14em' }}
                            />
                          </div>
                        ) : (
                          <div className="col-md-12">
                            <h4>No Profile Picture</h4>
                          </div>
                        )}
                      </div>
                      <CardBody>
                        <AgentName>{`${agent.firstName} ${agent.lastName}`}</AgentName>
                        <AgentOrg>{agent.orgName}</AgentOrg>
                        <div>{agent.orgAddress}</div>
                        <div>
                          <label>Phone: </label>
                          {formatPhoneNumber(agent.orgPhone)}
                        </div>
                        <div>
                          <label>Email: </label>
                          {agent.email}
                        </div>
                        <div>
                          {agent.frontEndInstructions?.releaseCell && (
                            <>
                              <label>Mobile:</label>
                              {formatPhoneNumber(agent.phone)}
                            </>
                          )}
                        </div>
                      </CardBody>
                    </StyledCard>
                  ))}
                  {agents.length === 0 && (
                    <div>
                      There is no listing agent associated with your property.
                    </div>
                  )}
                </CardsContainer>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const CardsContainer = styled.div`
  display: flex;
  gap: 2rem;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
`;

const StyledCard = styled(Card)`
  box-shadow: 3px 3px 10px #bbb;
  max-width: 400px;
  margin: 10px 0;
`;
const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 16px;
  color: #6a6c6f;
`;
const AgentName = styled.label`
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
`;
const AgentOrg = styled.div`
  margin: 1rem 0;
`;
export default MyAgent;
